/* override */
import React from 'react';
import { compose, omit } from 'ramda';
import { mapProps, pure, withHandlers, withState } from 'recompose';
import GameHoverCard from './GameHoverCard';

import style from './index.scss';

export const HOVER_TRANSITION_DURATION = 150;
export const JACKPOT_TRANSITION_DURATION = 150;

export const ASPECT_RATIO = 1.5;

export const withGameHoverDumb = Wrapped => ({
    commonGameCls = '',
    setHoverRef,
    isHover,
    className = '',
    code,
    showFavoriteIcon,
    getGameImageUrl,
    ...props
}) => (
    <Wrapped
        className={`${style.hoverContainer} ${className}`}
        commonGameCls={`${commonGameCls} ${isHover ? style.hiddenGame : ''}`}
        jackpotCls={`${style.jackpot} ${isHover ? style.hiddenGame : ''}`}
        {...{ code, showFavoriteIcon }}
        {...props}
    >
        {isHover && <GameHoverCard
            setGameInfoRef={setHoverRef}
            gameImageUrl={getGameImageUrl()}
            {...{
                code,
                showFavoriteIcon
            }}
        />}
    </Wrapped>
);

export default compose(
    pure,
    mapProps(omit(['dispatch'])),
    withState('isHover', 'setIsHover', false),
    withHandlers(({ setGameRef, setIsHover }) => {
        let gameRef;
        let hoverRef;
        let jackpotRef;
        let commonGameRef;
        let scale;
        let imageRef;

        const getGamePosition = () => {
            // in order to not count slide nav elements we need to filter out them from slide container
            const allChildren = [...gameRef.parentElement.childNodes]
                .filter(item => item.className.includes(style.hoverContainer));
            const childIndex = allChildren.indexOf(gameRef);

            return { isFirst: !childIndex, isLast: !(allChildren - childIndex - 1) };
        };

        const getScaleShift = () => {
            const gameWidth = commonGameRef.getBoundingClientRect().width;
            const hoverWidth = gameWidth * 1.2;
            scale = gameWidth / hoverWidth;

            return { shiftWidth: Math.floor(hoverWidth - gameWidth) };
        };

        const setInitialHoverStyles = () => {
            const scaleTransform = `scale(${scale})`;
            hoverRef.style.transform = scaleTransform;
            hoverRef.style.WebkitTransform = scaleTransform;
            gameRef.style.zIndex = '2';
        };

        const getTranslateParameters = ({ isFirst, isLast, shiftWidth }) => {
            let x = isFirst ? 0 : -shiftWidth;
            const shiftHeight = shiftWidth / ASPECT_RATIO;

            if (isFirst && isLast || !isFirst && !isLast) {
                x = -shiftWidth / 2;
            }

            return { x, y: -shiftHeight / 2 };
        };

        const setHoverStyles = ({ x, y }) => {
            const translate = `translate(${x}px, ${y}px)`;
            hoverRef.style.transform = translate;
            hoverRef.style.WebkitTransform = translate;
            hoverRef.style.transitionDuration = `${HOVER_TRANSITION_DURATION}ms`;
            hoverRef.style.webkitTransitionDuration = `${HOVER_TRANSITION_DURATION}ms`;
            hoverRef.classList.add(style.hover);
        };

        const setJackpotStyles = ({ isFirst, isLast, y, shiftWidth }) => {
            if (jackpotRef) {
                const jackpotHeight = jackpotRef.getBoundingClientRect().height;
                jackpotRef.style.top = `${-jackpotHeight / 2 + y}px`;

                if (isFirst) {
                    jackpotRef.style.right = `${-shiftWidth}px`;
                } else if (isLast) {
                    jackpotRef.style.left = `${-shiftWidth}px`;
                }

                jackpotRef.style.transitionDuration = `${JACKPOT_TRANSITION_DURATION}ms`;
                jackpotRef.style.webkitTransitionDuration = `${JACKPOT_TRANSITION_DURATION}ms`;
            }
        };

        const setScale = () => {
            const { isFirst, isLast } = getGamePosition();
            const { shiftWidth } = getScaleShift();
            setInitialHoverStyles();

            // eslint-disable-next-line @babel/no-unused-expressions
            hoverRef.offsetHeight; /* trigger reflow */

            const { x, y } = getTranslateParameters({ isFirst, isLast, shiftWidth });
            setHoverStyles({ x, y });
            setJackpotStyles({ isFirst, isLast, y, shiftWidth });
        };

        const unsetScale = () => {
            const scaleTransform = `scale(${scale})`;
            hoverRef.style.transform = scaleTransform;
            hoverRef.style.WebkitTransform = scaleTransform;
            hoverRef.classList.remove(style.hover);

            if (jackpotRef) {
                jackpotRef.style.top = '';
                jackpotRef.style.left = '';
                jackpotRef.style.right = '';
            }

            gameRef.style.zIndex = '';
        };

        const hideHover = () => {
            unsetScale();
            setTimeout(() => setIsHover(false), HOVER_TRANSITION_DURATION);
        };

        const onWindowMouseMove = (e) => {
            const { clientX, clientY, currentTarget } = e;

            if (hoverRef && hoverRef !== currentTarget) {
                const { left, right, top, bottom } = hoverRef.getBoundingClientRect();

                if (clientX < left || clientX > right || clientY < top || clientY > bottom) {
                    window.removeEventListener('mousemove', onWindowMouseMove);
                    hideHover();
                }
            }
        };

        const onMouseMove = ({ isHover }) => (e) => {
            if (!isHover) {
                e.stopPropagation(); // prevent newly subscribed handler from triggering
                setIsHover(true, setScale);
                window.addEventListener('mousemove', onWindowMouseMove);
            }
        };

        return {
            setGameRef: () => (ref) => {
                gameRef = ref;
                setGameRef && setGameRef(ref);
            },
            setHoverRef: () => ref => hoverRef = ref,
            setNgpJackpotRef: () => ref => jackpotRef = ref,
            setGameImageRef: () => ref => commonGameRef = ref,
            setImageRef: () => ref => imageRef = ref,
            getGameImageUrl: () => () => imageRef?.src,
            onMouseMove
        };
    }),
    withGameHoverDumb
);
